import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import ReactPlayer from "react-player";
import { CircularProgressCenter } from "@camberi/firecms";
import { PreviewImage } from "../../PreviewImage";
import MetadataViewer from "./MetadataViewer";
import { nonselect } from "../../../utils/basicFunctions";
import { useRafVideoSync } from "./useRafVideoSync";

type ComparisonMedia = {
	label?: string;
	version: any;
	versionId?: string;
	previewAssetId: string;
	previewAssetIsVideo: boolean;
	previewAssetInfo: any;
	videoUrl: string;
};

type ComparisonMode = "side-by-side" | "overlay" | "wipe";
type SoloSide = "none" | "left" | "right";
type CompareWarmupState = "idle" | "warming" | "done" | "failed";

type PlayerComparisonStageProps = {
	primary: ComparisonMedia;
	compare: ComparisonMedia | null;
	playerState: any;
	layout?: "side-by-side" | "stacked";
	syncEnabled?: boolean;
	infoState?: "none" | "compact" | "metadata";
	gridState?: "none" | "grid" | "overscangrid";
	overscanZoomEnabled?: boolean;
	swapVisualSides?: boolean;
	videoFlipped?: boolean;
};

type PrimaryVideoPlayerProps = {
	playerRef: any;
	videoUrl: string;
	muted: boolean;
	playingValue: boolean;
	autoPlay: boolean;
	looping: boolean;
	seeking: boolean;
	setBuffering: (value: boolean) => void;
	play: () => void;
	pause: () => void;
	handleOnReady: () => void;
	handleProgress: () => void;
	handleEnded: () => void;
};

type FollowerVideoPlayerProps = {
	playerRef: any;
	videoUrl: string;
	playingValue?: boolean;
	playbackRate?: number;
	onReady?: () => void;
	onProgress?: (state: any) => void;
	onError?: (error: any) => void;
};

type Size = {
	width: number;
	height: number;
};

type Rect = {
	left: number;
	top: number;
	width: number;
	height: number;
};

const EMPTY_RECT: Rect = { left: 0, top: 0, width: 0, height: 0 };

const COMPARE_WARMUP_PLAYBACK_RATE = 8;
const COMPARE_WARMUP_VIDEO_SECONDS = 3;
const COMPARE_WARMUP_MAX_REALTIME_MS = 1800;

const reactPlayerConfig = {
	attributes: {
		disablePictureInPicture: true,
		controlsList: "nodownload",
		preload: "auto",
		style: {
			width: "100%",
			height: "100%",
			objectFit: "contain",
			backgroundColor: "black",
			display: "block",
		},
	},
};

function normalizeText(value: any) {
	return String(value || "").trim();
}

function getFirstText(...values: any[]) {
	for (const value of values) {
		const text = normalizeText(value);

		if (text.length) {
			return text;
		}
	}

	return "";
}

function formatVersionNameCandidate(value: any) {
	const text = normalizeText(value);

	if (!text.length) {
		return "";
	}

	if (/^v\d+$/i.test(text)) {
		return text.toLowerCase();
	}

	if (/^\d+$/.test(text)) {
		return `v${text}`;
	}

	return text;
}

function getVersionBadgeLabel(media: ComparisonMedia | null, fallbackLabel: string) {
	if (!media) {
		return fallbackLabel;
	}

	const version = media.version || {};
	const values = version?.values || {};

	const directName = getFirstText(
		version?.name,
		version?.label,
		version?.version_name,
		version?.versionName,
		version?.display_name,
		version?.displayName,
		values?.name,
		values?.label,
		values?.version_name,
		values?.versionName,
		values?.display_name,
		values?.displayName
	);

	const formattedDirectName = formatVersionNameCandidate(directName);

	if (formattedDirectName.length) {
		return formattedDirectName;
	}

	const versionNumber = getFirstText(version?.version, version?.version_number, version?.versionNumber, version?.number, values?.version, values?.version_number, values?.versionNumber, values?.number);

	const formattedVersionNumber = formatVersionNameCandidate(versionNumber);

	if (formattedVersionNumber.length) {
		return formattedVersionNumber;
	}

	const label = formatVersionNameCandidate(media.label);

	if (label.length && !label.includes("/") && !label.includes("_")) {
		return label;
	}

	return fallbackLabel;
}

function getVersionComment(media: ComparisonMedia | null) {
	if (!media) {
		return "";
	}

	const version = media.version || {};
	const values = version?.values || {};

	return getFirstText(
		version?.commit_message,
		version?.commitMessage,
		version?.comment,
		version?.comments,
		version?.description,
		version?.note,
		version?.notes,
		values?.commit_message,
		values?.commitMessage,
		values?.comment,
		values?.comments,
		values?.description,
		values?.note,
		values?.notes
	);
}

function parseNumberish(value: unknown): number | null {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	if (typeof value === "string") {
		const cleaned = value.trim().replace("%", "");

		if (!cleaned) {
			return null;
		}

		const parsed = Number(cleaned);

		if (Number.isFinite(parsed)) {
			return parsed;
		}
	}

	return null;
}

function parseOverscanValue(value: unknown): number | null {
	const parsed = parseNumberish(value);

	if (parsed === null || parsed < 0) {
		return null;
	}

	if (typeof value === "string" && value.includes("%")) {
		return parsed / 100;
	}

	if (parsed > 1) {
		return parsed / 100;
	}

	return parsed;
}

function resolveOverscanFromResolution(resolution?: any): number | null {
	const dataX = Number(resolution?.data?.x) || 0;
	const dataY = Number(resolution?.data?.y) || 0;
	const dispX = Number(resolution?.disp?.x) || 0;
	const dispY = Number(resolution?.disp?.y) || 0;

	const ratios: number[] = [];

	if (dataX > 0 && dispX > 0 && dataX > dispX) {
		ratios.push(dataX / dispX - 1);
	}

	if (dataY > 0 && dispY > 0 && dataY > dispY) {
		ratios.push(dataY / dispY - 1);
	}

	if (!ratios.length) {
		return null;
	}

	return Math.max(...ratios);
}

function resolveOverscanPercentageFromVersionMetadata(versionMetadata?: any): number {
	const shotMetadata = versionMetadata?.render_metadata?.shot;
	const camera = shotMetadata?.camera;
	const resolution = shotMetadata?.resolution;

	const pathTracerOverscan = parseOverscanValue(camera?.PathTracer?.overscanPercent);

	if (pathTracerOverscan !== null && pathTracerOverscan > 0) {
		return pathTracerOverscan;
	}

	const cameraOverscan = parseOverscanValue(camera?.overscanPercentage);

	if (cameraOverscan !== null && cameraOverscan > 0) {
		return cameraOverscan;
	}

	const resolutionOverscan = resolveOverscanFromResolution(resolution);

	if (resolutionOverscan !== null && resolutionOverscan > 0) {
		return resolutionOverscan;
	}

	return 0;
}

function firstFiniteNumber(...values: any[]): number {
	for (const value of values) {
		const parsed = parseNumberish(value);

		if (parsed !== null && parsed > 0) {
			return parsed;
		}
	}

	return 0;
}

function getPreviewAspectRatio(previewAssetInfo?: any, versionMetadata?: any): number {
	const shotResolution = versionMetadata?.render_metadata?.shot?.resolution;

	const candidates: Array<[any, any]> = [
		[previewAssetInfo?.metadata?.blob?.width, previewAssetInfo?.metadata?.blob?.height],
		[previewAssetInfo?.metadata?.blob?.videoWidth, previewAssetInfo?.metadata?.blob?.videoHeight],
		[previewAssetInfo?.metadata?.width, previewAssetInfo?.metadata?.height],
		[previewAssetInfo?.metadata?.videoWidth, previewAssetInfo?.metadata?.videoHeight],
		[previewAssetInfo?.width, previewAssetInfo?.height],
		[previewAssetInfo?.videoWidth, previewAssetInfo?.videoHeight],
		[previewAssetInfo?.source?.width, previewAssetInfo?.source?.height],
		[previewAssetInfo?.metadata?.streams?.[0]?.width, previewAssetInfo?.metadata?.streams?.[0]?.height],
		[shotResolution?.data?.x, shotResolution?.data?.y],
		[shotResolution?.disp?.x, shotResolution?.disp?.y],
	];

	for (const [rawWidth, rawHeight] of candidates) {
		const width = firstFiniteNumber(rawWidth);
		const height = firstFiniteNumber(rawHeight);

		if (width > 0 && height > 0) {
			return width / height;
		}
	}

	return 16 / 9;
}

function roundSize(value: number) {
	return Math.round(value * 100) / 100;
}

function clamp(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max);
}

function isUsableRect(rect: Rect) {
	return rect.width > 0 && rect.height > 0;
}

function getNowMs() {
	if (typeof performance !== "undefined" && typeof performance.now === "function") {
		return performance.now();
	}

	return Date.now();
}

function getCompareWarmupTargetSeconds(video: HTMLVideoElement | null) {
	const duration = Number(video?.duration);

	if (Number.isFinite(duration) && duration > 0) {
		return Math.max(0, Math.min(COMPARE_WARMUP_VIDEO_SECONDS, duration - 0.05));
	}

	return COMPARE_WARMUP_VIDEO_SECONDS;
}

function getContainRect(area: Rect, aspectRatio: number, alignX: number = 0.5, alignY: number = 0.5): Rect {
	if (!area.width || !area.height || !aspectRatio || !Number.isFinite(aspectRatio)) {
		return EMPTY_RECT;
	}

	const areaAspectRatio = area.width / area.height;

	let width = area.width;
	let height = area.height;

	if (areaAspectRatio > aspectRatio) {
		height = area.height;
		width = height * aspectRatio;
	} else {
		width = area.width;
		height = width / aspectRatio;
	}

	return {
		left: roundSize(area.left + (area.width - width) * alignX),
		top: roundSize(area.top + (area.height - height) * alignY),
		width: roundSize(width),
		height: roundSize(height),
	};
}

function useElementSize(ref: React.RefObject<HTMLElement>) {
	const [size, setSize] = useState<Size>({ width: 0, height: 0 });

	useEffect(() => {
		const element = ref.current;

		if (!element) {
			return;
		}

		let rafId = 0;
		let resizeObserver: ResizeObserver | null = null;

		const measure = () => {
			const rect = element.getBoundingClientRect();

			const nextSize = {
				width: roundSize(Number(rect.width) || 0),
				height: roundSize(Number(rect.height) || 0),
			};

			setSize((prev) => {
				if (Math.abs(prev.width - nextSize.width) < 0.5 && Math.abs(prev.height - nextSize.height) < 0.5) {
					return prev;
				}

				return nextSize;
			});
		};

		const requestMeasure = () => {
			cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(measure);
		};

		requestMeasure();

		if (typeof ResizeObserver !== "undefined") {
			resizeObserver = new ResizeObserver(requestMeasure);
			resizeObserver.observe(element);
		}

		window.addEventListener("resize", requestMeasure);

		return () => {
			cancelAnimationFrame(rafId);
			window.removeEventListener("resize", requestMeasure);

			if (resizeObserver) {
				resizeObserver.disconnect();
			}
		};
	}, [ref]);

	return size;
}

const PrimaryVideoPlayer = React.memo(
	function PrimaryVideoPlayer({ playerRef, videoUrl, muted, playingValue, autoPlay, looping, seeking, setBuffering, play, pause, handleOnReady, handleProgress, handleEnded }: PrimaryVideoPlayerProps) {
		if (!videoUrl.length) {
			return null;
		}

		return (
			<ReactPlayer
				ref={playerRef}
				autoPlay={autoPlay}
				playsinline
				muted={muted}
				playing={playingValue}
				onBuffer={() => setBuffering(true)}
				onBufferEnd={() => setBuffering(false)}
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					outline: "none!important",
					opacity: 1,
					backgroundColor: "black",
					transform: "translateZ(0)",
					willChange: "transform",
					backfaceVisibility: "hidden",
				}}
				width="100%"
				height="100%"
				config={{ file: { ...reactPlayerConfig } }}
				url={videoUrl}
				onPlay={play}
				onPause={pause}
				onReady={handleOnReady}
				progressInterval={30}
				onProgress={handleProgress}
				loop={looping && !seeking}
				onEnded={handleEnded}
				onError={(e: any) => console.log(e)}
			/>
		);
	},
	(prev, next) => {
		return (
			prev.playerRef === next.playerRef &&
			prev.videoUrl === next.videoUrl &&
			prev.muted === next.muted &&
			prev.playingValue === next.playingValue &&
			prev.autoPlay === next.autoPlay &&
			prev.looping === next.looping &&
			prev.seeking === next.seeking &&
			prev.setBuffering === next.setBuffering &&
			prev.play === next.play &&
			prev.pause === next.pause &&
			prev.handleOnReady === next.handleOnReady &&
			prev.handleProgress === next.handleProgress &&
			prev.handleEnded === next.handleEnded
		);
	}
);

const FollowerVideoPlayer = React.memo(
	function FollowerVideoPlayer({ playerRef, videoUrl, playingValue = false, playbackRate = 1, onReady, onProgress, onError }: FollowerVideoPlayerProps) {
		if (!videoUrl.length) {
			return null;
		}

		return (
			<ReactPlayer
				ref={playerRef}
				autoPlay={playingValue}
				playsinline
				muted={true}
				playing={playingValue}
				playbackRate={playbackRate}
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					outline: "none!important",
					opacity: 1,
					backgroundColor: "black",
					transform: "translateZ(0)",
					willChange: "transform",
					backfaceVisibility: "hidden",
				}}
				width="100%"
				height="100%"
				config={{ file: { ...reactPlayerConfig } }}
				url={videoUrl}
				onReady={onReady}
				progressInterval={100}
				onProgress={onProgress}
				onError={onError || ((e: any) => console.log(e))}
			/>
		);
	},
	(prev, next) => {
		return (
			prev.playerRef === next.playerRef &&
			prev.videoUrl === next.videoUrl &&
			prev.playingValue === next.playingValue &&
			prev.playbackRate === next.playbackRate &&
			prev.onReady === next.onReady &&
			prev.onProgress === next.onProgress &&
			prev.onError === next.onError
		);
	}
);

type GridProps = {
	borderSize: number;
	showOuterBorder?: boolean;
};

function Grid({ borderSize, showOuterBorder = true }: GridProps) {
	const outerBorderSize = borderSize * 2;

	const renderRow = (isMiddle: boolean) => (
		<div style={{ height: "calc(100% / 3)", display: "flex", width: "100%" }}>
			{[0, 1, 2].map((col) => {
				const borderWidths = isMiddle ? `${borderSize}px ${col === 0 ? 0 : 0}px ${borderSize}px ${col === 0 ? 0 : borderSize}px` : col === 0 ? "0px 0px 0px 0px" : `0px 0px 0px ${borderSize}px`;

				return <div key={col} style={{ height: "100%", width: "100%", border: `${borderSize}px solid white`, borderWidth: borderWidths, boxSizing: "border-box" }} />;
			})}
		</div>
	);

	return (
		<div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", boxSizing: "border-box", pointerEvents: "none" }}>
			{renderRow(false)}
			{renderRow(true)}
			{renderRow(false)}

			{showOuterBorder && <div style={{ position: "absolute", inset: 0, border: `${outerBorderSize}px solid rgb(16, 185, 129)`, boxSizing: "border-box", pointerEvents: "none", zIndex: 10 }} />}
		</div>
	);
}

type FrameGridOverlayProps = {
	media: ComparisonMedia | null;
	gridState: "none" | "grid" | "overscangrid";
	infoState: "none" | "compact" | "metadata";
	overscanZoomActive: boolean;
};

function FrameGridOverlay({ media, gridState, infoState, overscanZoomActive }: FrameGridOverlayProps) {
	const versionMetadata = media?.version?.values?.version_metadata;
	const overscanPercentage = resolveOverscanPercentageFromVersionMetadata(versionMetadata);
	const displayWindowScale = 1 / (1 + overscanPercentage);
	const resolvedGridState = overscanPercentage === 0 && gridState === "overscangrid" ? "grid" : gridState;
	const showInfoOnlyOverlay = infoState !== "none" && resolvedGridState === "none";

	if (resolvedGridState === "none" && !showInfoOnlyOverlay) {
		return null;
	}

	const renderGridOverlay = (scale: number, showOuterBorder: boolean) => {
		const borderSize = 1 / scale;

		return (
			<Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", zIndex: 14 }}>
				<Box sx={{ position: "relative", width: "100%", height: "100%", transform: `scale(${scale})`, transformOrigin: "center center" }}>
					<Grid borderSize={borderSize} showOuterBorder={showOuterBorder} />
				</Box>
			</Box>
		);
	};

	return (
		<>
			{showInfoOnlyOverlay ? <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 14 }} /> : null}

			{overscanZoomActive ? (
				<>{resolvedGridState !== "none" ? renderGridOverlay(1, true) : null}</>
			) : (
				<>
					{resolvedGridState === "grid" ? renderGridOverlay(1, false) : null}
					{resolvedGridState === "overscangrid" ? renderGridOverlay(displayWindowScale, true) : null}
				</>
			)}
		</>
	);
}

type MediaLayerProps = {
	media: ComparisonMedia | null;
	kind: "primary" | "compare";
	playerRef?: any;
	comparePlayerRef?: any;
	muted?: boolean;
	playingValue?: boolean;
	autoPlay?: boolean;
	looping?: boolean;
	seeking?: boolean;
	setBuffering?: (value: boolean) => void;
	play?: () => void;
	pause?: () => void;
	handleOnReady?: () => void;
	handleProgress?: () => void;
	handleEnded?: () => void;
	handleCompareReady?: () => void;
	comparePlayingValue?: boolean;
	comparePlaybackRate?: number;
	handleCompareProgress?: (state: any) => void;
	handleCompareError?: (error: any) => void;
};

function MediaLayer({
	media,
	kind,
	playerRef,
	comparePlayerRef,
	muted = true,
	playingValue = false,
	autoPlay = false,
	looping = false,
	seeking = false,
	setBuffering = () => {},
	play = () => {},
	pause = () => {},
	handleOnReady = () => {},
	handleProgress = () => {},
	handleEnded = () => {},
	handleCompareReady,
	comparePlayingValue = false,
	comparePlaybackRate = 1,
	handleCompareProgress,
	handleCompareError,
}: MediaLayerProps) {
	if (!media) {
		return (
			<Box sx={{ position: "absolute", inset: 0, backgroundColor: "black", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.5)" }}>
				<Typography variant="body2">No comparison loaded</Typography>
			</Box>
		);
	}

	if (!media.previewAssetIsVideo) {
		return (
			<PreviewImage
				key={media.previewAssetId + "_comparison_preview_image_" + kind}
				version={media.version}
				assetId={media.previewAssetId}
				autoplay={false}
				is_visible={true}
				img_type={"full"}
				img_fit_view={true}
				strict_asset_id={true}
				sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "black" }}
			/>
		);
	}

	return (
		<>
			{kind === "primary" ? (
				<PrimaryVideoPlayer
					playerRef={playerRef}
					videoUrl={media.videoUrl}
					muted={muted}
					playingValue={playingValue}
					autoPlay={autoPlay}
					looping={looping}
					seeking={seeking}
					setBuffering={setBuffering}
					play={play}
					pause={pause}
					handleOnReady={handleOnReady}
					handleProgress={handleProgress}
					handleEnded={handleEnded}
				/>
			) : (
				<FollowerVideoPlayer playerRef={comparePlayerRef} videoUrl={media.videoUrl} playingValue={comparePlayingValue} playbackRate={comparePlaybackRate} onReady={handleCompareReady} onProgress={handleCompareProgress} onError={handleCompareError} />
			)}

			{!media.videoUrl.length ? (
				<Box sx={{ position: "absolute", inset: 0, backgroundColor: "black", zIndex: 10 }}>
					<CircularProgressCenter />
				</Box>
			) : null}
		</>
	);
}

type ComparisonBadgeProps = {
	media: ComparisonMedia | null;
	fallbackLabel: string;
	sideLabel?: string;
	left?: number;
	right?: number;
	top?: number;
	width?: number;
	align?: "left" | "right";
	color?: string;
};

function ComparisonBadge({ media, fallbackLabel, sideLabel, left, right, top = 8, width, align = "left", color = "white" }: ComparisonBadgeProps) {
	const badgeLabel = getVersionBadgeLabel(media, fallbackLabel);
	const comment = getVersionComment(media);
	const alignRight = align === "right";

	return (
		<Box
			sx={{
				position: "absolute",
				top,
				left,
				right,
				width: width ?? "max-content",
				zIndex: 70,
				pointerEvents: "none",
				display: "flex",
				flexDirection: "column",
				alignItems: alignRight ? "flex-end" : "flex-start",
				minWidth: 0,
			}}
		>
			<Box
				sx={{
					px: 1,
					py: 0.35,
					borderRadius: 1,
					backgroundColor: "rgba(0,0,0,0.65)",
					color,
					display: "inline-flex",
					gap: 0.5,
					alignItems: "center",
					maxWidth: width ? "100%" : "320px",
					minWidth: 0,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						fontWeight: 700,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						minWidth: 0,
					}}
				>
					{badgeLabel}
				</Typography>

				{sideLabel ? (
					<Typography variant="caption" sx={{ opacity: 0.7, flex: "0 0 auto" }}>
						{sideLabel}
					</Typography>
				) : null}
			</Box>

			{comment ? (
				<Box
					sx={{
						mt: 0.25,
						px: 1,
						py: 0.35,
						borderRadius: 1,
						backgroundColor: "rgba(0,0,0,0.65)",
						color,
						maxWidth: width ? "100%" : "320px",
						minWidth: 0,
					}}
				>
					<Typography
						variant="caption"
						title={comment}
						sx={{
							display: "block",
							opacity: 0.78,
							fontSize: "0.68rem",
							lineHeight: 1.15,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							maxWidth: "100%",
							textAlign: alignRight ? "right" : "left",
						}}
					>
						{comment}
					</Typography>
				</Box>
			) : null}
		</Box>
	);
}

type MediaFrameProps = {
	rect: Rect;
	media: ComparisonMedia | null;
	kind: "primary" | "compare";
	visible: boolean;
	opacity?: number;
	zIndex: number;
	clipPath?: string;
	overscanZoomEnabled: boolean;
	videoFlipped?: boolean;
	playerRef?: any;
	comparePlayerRef?: any;
	muted?: boolean;
	playingValue?: boolean;
	autoPlay?: boolean;
	looping?: boolean;
	seeking?: boolean;
	setBuffering?: (value: boolean) => void;
	play?: () => void;
	pause?: () => void;
	handleOnReady?: () => void;
	handleProgress?: () => void;
	handleEnded?: () => void;
	handleCompareReady?: () => void;
	comparePlayingValue?: boolean;
	comparePlaybackRate?: number;
	handleCompareProgress?: (state: any) => void;
	handleCompareError?: (error: any) => void;
};

function MediaFrame({
	rect,
	media,
	kind,
	visible,
	opacity = 1,
	zIndex,
	clipPath,
	overscanZoomEnabled,
	videoFlipped = false,
	playerRef,
	comparePlayerRef,
	muted,
	playingValue,
	autoPlay,
	looping,
	seeking,
	setBuffering,
	play,
	pause,
	handleOnReady,
	handleProgress,
	handleEnded,
	handleCompareReady,
	comparePlayingValue = false,
	comparePlaybackRate = 1,
	handleCompareProgress,
	handleCompareError,
}: MediaFrameProps) {
	const versionMetadata = media?.version?.values?.version_metadata;
	const overscanPercentage = resolveOverscanPercentageFromVersionMetadata(versionMetadata);
	const shouldZoomToNonOverscan = overscanZoomEnabled && overscanPercentage > 0;
	const safeOverscanZoom = shouldZoomToNonOverscan ? 1 + overscanPercentage : 1;
	const shouldFlipMedia = videoFlipped && Boolean(media?.previewAssetIsVideo);
	const mediaTransform = shouldFlipMedia ? `scaleX(-1) scale(${safeOverscanZoom})` : `scale(${safeOverscanZoom})`;
	const hasRect = isUsableRect(rect);

	return (
		<Box
			sx={{
				position: "absolute",
				left: hasRect ? rect.left : 0,
				top: hasRect ? rect.top : 0,
				width: hasRect ? rect.width : "100%",
				height: hasRect ? rect.height : "100%",
				backgroundColor: "black",
				overflow: "hidden",
				opacity: visible ? opacity : 0,
				visibility: visible ? "visible" : "hidden",
				pointerEvents: "none",
				zIndex,
				clipPath,
				transition: "none",
			}}
		>
			<Box sx={{ position: "absolute", inset: 0, backgroundColor: "black", overflow: "hidden", transform: mediaTransform, transformOrigin: "center center" }}>
				<MediaLayer
					media={media}
					kind={kind}
					playerRef={playerRef}
					comparePlayerRef={comparePlayerRef}
					muted={muted}
					playingValue={playingValue}
					autoPlay={autoPlay}
					looping={looping}
					seeking={seeking}
					setBuffering={setBuffering}
					play={play}
					pause={pause}
					handleOnReady={handleOnReady}
					handleProgress={handleProgress}
					handleEnded={handleEnded}
					handleCompareReady={handleCompareReady}
					comparePlayingValue={comparePlayingValue}
					comparePlaybackRate={comparePlaybackRate}
					handleCompareProgress={handleCompareProgress}
					handleCompareError={handleCompareError}
				/>
			</Box>

			{shouldZoomToNonOverscan ? <Box sx={{ position: "absolute", inset: 0, border: "2px solid rgb(16, 185, 129)", boxSizing: "border-box", pointerEvents: "none", zIndex: 13 }} /> : null}
		</Box>
	);
}

type FrameDecorationsProps = {
	rect: Rect;
	media: ComparisonMedia | null;
	infoState: "none" | "compact" | "metadata";
	gridState: "none" | "grid" | "overscangrid";
	overscanZoomEnabled: boolean;
};

function FrameDecorations({ rect, media, infoState, gridState, overscanZoomEnabled }: FrameDecorationsProps) {
	if (!isUsableRect(rect)) {
		return null;
	}

	const versionMetadata = media?.version?.values?.version_metadata;
	const overscanPercentage = resolveOverscanPercentageFromVersionMetadata(versionMetadata);
	const shouldZoomToNonOverscan = overscanZoomEnabled && overscanPercentage > 0;

	return (
		<Box sx={{ position: "absolute", left: rect.left, top: rect.top, width: rect.width, height: rect.height, pointerEvents: "none", zIndex: 60 }}>
			<FrameGridOverlay media={media} gridState={gridState} infoState={infoState} overscanZoomActive={shouldZoomToNonOverscan} />

			{infoState === "metadata" && media?.version?.values?.version_metadata ? (
				<Box sx={{ position: "absolute", inset: 0, zIndex: 25, background: "rgba(0,0,0,0.9)", overflowY: "scroll", pointerEvents: "auto" }}>
					<MetadataViewer data={{ version_metadata: media.version.values.version_metadata }} expandDepth={25} style={{}} placeholder="Loading JSON..." />
				</Box>
			) : null}
		</Box>
	);
}

function ComparisonModeControls({
	mode,
	soloSide,
	onModeChange,
	onSoloSideChange,
	onResetWipe,
}: {
	mode: ComparisonMode;
	soloSide: SoloSide;
	onModeChange: (mode: ComparisonMode) => void;
	onSoloSideChange: (side: SoloSide) => void;
	onResetWipe: () => void;
}) {
	return (
		<Box sx={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 80, display: "flex", gap: 1, alignItems: "center", backgroundColor: "rgba(0,0,0,0.45)", borderRadius: 1, p: 0.5 }}>
			<ButtonGroup size="small" variant="outlined">
				<Tooltip title="Side by side. Shortcut: S">
					<Button
						color={mode === "side-by-side" && soloSide === "none" ? "secondary" : "inherit"}
						onClick={() => {
							onSoloSideChange("none");
							onModeChange("side-by-side");
						}}
					>
						Side
					</Button>
				</Tooltip>

				<Tooltip title="Overlay. Shortcut: O. Use mouse wheel to adjust overlay opacity.">
					<Button
						color={mode === "overlay" && soloSide === "none" ? "secondary" : "inherit"}
						onClick={() => {
							onSoloSideChange("none");
							onModeChange("overlay");
						}}
					>
						Overlay
					</Button>
				</Tooltip>

				<Tooltip title="Wipe. Shortcut: W. Drag horizontally to move. Use mouse wheel to fade the left wipe area. Double click to reset.">
					<Button
						color={mode === "wipe" && soloSide === "none" ? "secondary" : "inherit"}
						onClick={() => {
							onSoloSideChange("none");
							onModeChange("wipe");
						}}
					>
						Wipe
					</Button>
				</Tooltip>
			</ButtonGroup>

			<ButtonGroup size="small" variant="outlined">
				<Tooltip title="Show A full frame. Shortcut: A">
					<Button color={soloSide === "left" ? "secondary" : "inherit"} onClick={() => onSoloSideChange("left")}>
						A
					</Button>
				</Tooltip>

				<Tooltip title="Show B full frame. Shortcut: B">
					<Button color={soloSide === "right" ? "secondary" : "inherit"} onClick={() => onSoloSideChange("right")}>
						B
					</Button>
				</Tooltip>
			</ButtonGroup>

			{mode === "wipe" ? (
				<Tooltip title="Reset wipe">
					<Button size="small" variant="outlined" color="inherit" onClick={onResetWipe}>
						Reset
					</Button>
				</Tooltip>
			) : null}
		</Box>
	);
}

export function PlayerComparisonStage({
	primary,
	compare,
	playerState,
	layout = "side-by-side",
	syncEnabled = true,
	infoState = "none",
	gridState = "none",
	overscanZoomEnabled = false,
	swapVisualSides = false,
	videoFlipped = false,
}: PlayerComparisonStageProps) {
	const comparePlayerRef = useRef<any>();
	const stageRef = useRef<HTMLDivElement | null>(null);
	const wipeDragRef = useRef({ pointerId: -1, startX: 0, startY: 0, moved: false });
	const compareWarmupStartedAtMsRef = useRef(0);

	const stageSize = useElementSize(stageRef);

	const [comparisonMode, setComparisonMode] = useState<ComparisonMode>("side-by-side");
	const [soloSide, setSoloSide] = useState<SoloSide>("none");
	const [wipePosition, setWipePosition] = useState<number>(0.5);
	const [overlayOpacity, setOverlayOpacity] = useState<number>(0.5);
	const [wipeTopOpacity, setWipeTopOpacity] = useState<number>(1);
	const [compareWarmupState, setCompareWarmupState] = useState<CompareWarmupState>("idle");

	const { playerRef, playing, seeking, prefPlaying, looping, loopRangeActive, muted, setBuffering, play, pause, handleTogglePlayPause, handleProgress, handleOnReady, handleEnded } = playerState;

	const baseCompareVideoSyncEnabled = syncEnabled && Boolean(primary?.videoUrl) && Boolean(compare?.videoUrl) && Boolean(primary?.previewAssetIsVideo) && Boolean(compare?.previewAssetIsVideo);
	const compareWarmupActive = baseCompareVideoSyncEnabled && compareWarmupState === "warming";
	const compareWarmupFinished = compareWarmupState === "done" || compareWarmupState === "failed";

	useRafVideoSync({
		enabled: baseCompareVideoSyncEnabled && compareWarmupFinished,
		primaryPlayerRef: playerRef,
		followerPlayerRef: comparePlayerRef,
		primaryVideoUrl: primary?.videoUrl,
		followerVideoUrl: compare?.videoUrl,
		primaryAssetInfo: primary?.previewAssetInfo,
		followerAssetInfo: compare?.previewAssetInfo,
	});

	useEffect(() => {
		compareWarmupStartedAtMsRef.current = 0;
		setCompareWarmupState("idle");
	}, [primary?.videoUrl, compare?.videoUrl]);

	const finishCompareWarmup = React.useCallback((state: CompareWarmupState = "done") => {
		const compareVideo = comparePlayerRef.current?.getInternalPlayer?.();

		if (compareVideo) {
			try {
				compareVideo.pause();
			} catch {}

			try {
				compareVideo.playbackRate = 1;
			} catch {}
		}

		setCompareWarmupState((prev) => {
			if (prev === "done" || prev === "failed") {
				return prev;
			}

			return state;
		});
	}, []);

	const startCompareWarmup = React.useCallback(() => {
		if (!baseCompareVideoSyncEnabled || compareWarmupState !== "idle") {
			return;
		}

		const compareVideo = comparePlayerRef.current?.getInternalPlayer?.();

		if (!compareVideo || compareVideo.readyState < 1) {
			return;
		}

		compareWarmupStartedAtMsRef.current = getNowMs();

		try {
			compareVideo.pause();
		} catch {}

		try {
			compareVideo.muted = true;
		} catch {}

		try {
			compareVideo.playbackRate = COMPARE_WARMUP_PLAYBACK_RATE;
		} catch {}

		try {
			compareVideo.currentTime = 0;
		} catch {}

		setCompareWarmupState("warming");
	}, [baseCompareVideoSyncEnabled, compareWarmupState]);

	const handleCompareReady = React.useCallback(() => {
		const compareVideo = comparePlayerRef.current?.getInternalPlayer?.();

		if (!compareVideo) {
			return;
		}

		if (baseCompareVideoSyncEnabled && compareWarmupState === "idle") {
			startCompareWarmup();
			return;
		}

		if (compareWarmupState !== "warming") {
			try {
				compareVideo.pause();
			} catch {}
		}
	}, [baseCompareVideoSyncEnabled, compareWarmupState, startCompareWarmup]);

	useEffect(() => {
		if (baseCompareVideoSyncEnabled && compareWarmupState === "idle") {
			startCompareWarmup();
		}
	}, [baseCompareVideoSyncEnabled, compareWarmupState, startCompareWarmup]);

	useEffect(() => {
		if (!compareWarmupActive) {
			return;
		}

		const timeoutId = window.setTimeout(() => {
			finishCompareWarmup("done");
		}, COMPARE_WARMUP_MAX_REALTIME_MS);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [compareWarmupActive, finishCompareWarmup]);

	useEffect(() => {
		if (!baseCompareVideoSyncEnabled && compareWarmupState === "warming") {
			finishCompareWarmup("failed");
		}
	}, [baseCompareVideoSyncEnabled, compareWarmupState, finishCompareWarmup]);

	const handleCompareProgress = React.useCallback(
		(state: any) => {
			if (!compareWarmupActive) {
				return;
			}

			const compareVideo = comparePlayerRef.current?.getInternalPlayer?.() as HTMLVideoElement | null;
			const targetSeconds = getCompareWarmupTargetSeconds(compareVideo);
			const playedSeconds = Number(state?.playedSeconds);
			const currentTime = Number.isFinite(playedSeconds) ? playedSeconds : Number(compareVideo?.currentTime) || 0;

			if (targetSeconds <= 0 || currentTime >= targetSeconds) {
				finishCompareWarmup("done");
			}
		},
		[compareWarmupActive, finishCompareWarmup]
	);

	const handleCompareError = React.useCallback(
		(error: any) => {
			console.log(error);

			if (compareWarmupState === "warming") {
				finishCompareWarmup("failed");
			}
		},
		[compareWarmupState, finishCompareWarmup]
	);

	const resetWipe = React.useCallback(() => {
		setWipePosition(0.5);
		setWipeTopOpacity(1);
	}, []);

	useEffect(() => {
		const handleKeyDown = (evt: KeyboardEvent) => {
			if (evt.metaKey || evt.ctrlKey || evt.altKey) {
				return;
			}

			const key = evt.key.toLowerCase();

			if (key === "s") {
				evt.preventDefault();
				evt.stopPropagation();
				setSoloSide("none");
				setComparisonMode("side-by-side");
				return;
			}

			if (key === "o") {
				evt.preventDefault();
				evt.stopPropagation();
				setSoloSide("none");
				setComparisonMode("overlay");
				return;
			}

			if (key === "w") {
				evt.preventDefault();
				evt.stopPropagation();
				setSoloSide("none");
				setComparisonMode("wipe");
				return;
			}

			if (key === "a") {
				evt.preventDefault();
				evt.stopPropagation();
				setSoloSide("left");
				return;
			}

			if (key === "b") {
				evt.preventDefault();
				evt.stopPropagation();
				setSoloSide("right");
			}
		};

		window.addEventListener("keydown", handleKeyDown, true);

		return () => {
			window.removeEventListener("keydown", handleKeyDown, true);
		};
	}, []);

	const primaryAspectRatio = getPreviewAspectRatio(primary?.previewAssetInfo, primary?.version?.values?.version_metadata);
	const compareAspectRatio = getPreviewAspectRatio(compare?.previewAssetInfo, compare?.version?.values?.version_metadata);

	const visualAMedia = swapVisualSides ? compare : primary;
	const visualBMedia = swapVisualSides ? primary : compare;
	const visualAAspectRatio = swapVisualSides ? compareAspectRatio : primaryAspectRatio;
	const visualBAspectRatio = swapVisualSides ? primaryAspectRatio : compareAspectRatio;

	const primaryVisualSide: "left" | "right" = swapVisualSides ? "right" : "left";
	const compareVisualSide: "left" | "right" = swapVisualSides ? "left" : "right";

	const isStacked = layout === "stacked";

	const layoutRects = useMemo(() => {
		const rootArea: Rect = {
			left: 0,
			top: 0,
			width: stageSize.width,
			height: stageSize.height,
		};

		if (!stageSize.width || !stageSize.height) {
			return {
				primaryRect: EMPTY_RECT,
				compareRect: EMPTY_RECT,
				decorationRect: EMPTY_RECT,
				activeMedia: visualAMedia,
			};
		}

		if (soloSide !== "none" || comparisonMode !== "side-by-side") {
			const activeMedia = soloSide === "right" ? visualBMedia : visualAMedia;
			const activeAspectRatio = soloSide === "right" ? visualBAspectRatio : visualAAspectRatio;
			const sharedRect = getContainRect(rootArea, activeAspectRatio, 0.5, 0.5);

			return {
				primaryRect: sharedRect,
				compareRect: sharedRect,
				decorationRect: sharedRect,
				activeMedia,
			};
		}

		if (isStacked) {
			const topArea: Rect = {
				left: 0,
				top: 0,
				width: stageSize.width,
				height: stageSize.height / 2,
			};

			const bottomArea: Rect = {
				left: 0,
				top: stageSize.height / 2,
				width: stageSize.width,
				height: stageSize.height / 2,
			};

			const primaryArea = primaryVisualSide === "left" ? topArea : bottomArea;
			const compareArea = compareVisualSide === "left" ? topArea : bottomArea;

			return {
				primaryRect: getContainRect(primaryArea, primaryAspectRatio, 0.5, primaryVisualSide === "left" ? 1 : 0),
				compareRect: getContainRect(compareArea, compareAspectRatio, 0.5, compareVisualSide === "left" ? 1 : 0),
				decorationRect: EMPTY_RECT,
				activeMedia: visualAMedia,
			};
		}

		const leftArea: Rect = {
			left: 0,
			top: 0,
			width: stageSize.width / 2,
			height: stageSize.height,
		};

		const rightArea: Rect = {
			left: stageSize.width / 2,
			top: 0,
			width: stageSize.width / 2,
			height: stageSize.height,
		};

		const primaryArea = primaryVisualSide === "left" ? leftArea : rightArea;
		const compareArea = compareVisualSide === "left" ? leftArea : rightArea;

		return {
			primaryRect: getContainRect(primaryArea, primaryAspectRatio, primaryVisualSide === "left" ? 1 : 0, 0.5),
			compareRect: getContainRect(compareArea, compareAspectRatio, compareVisualSide === "left" ? 1 : 0, 0.5),
			decorationRect: EMPTY_RECT,
			activeMedia: visualAMedia,
		};
	}, [compareAspectRatio, compareVisualSide, comparisonMode, isStacked, primaryAspectRatio, primaryVisualSide, soloSide, stageSize.height, stageSize.width, visualAAspectRatio, visualAMedia, visualBAspectRatio, visualBMedia]);

	const { primaryRect, compareRect, decorationRect, activeMedia } = layoutRects;

	const isWipeMode = comparisonMode === "wipe" && soloSide === "none";
	const isOverlayMode = comparisonMode === "overlay" && soloSide === "none";

	const topX = wipePosition;
	const bottomX = wipePosition;

	const visualAClipPath = isWipeMode ? `polygon(0% 0%, ${topX * 100}% 0%, ${bottomX * 100}% 100%, 0% 100%)` : undefined;

	const primaryClipPath = swapVisualSides ? undefined : visualAClipPath;
	const compareClipPath = swapVisualSides ? visualAClipPath : undefined;

	const isSharedFrameMode = soloSide !== "none" || comparisonMode !== "side-by-side";
	const compareHasRenderableMedia = Boolean(compare && (!compare.previewAssetIsVideo || compare.videoUrl.length));

	const showPrimary = soloSide === "none" || soloSide === primaryVisualSide;
	const showCompare = Boolean(compare) && (soloSide === "none" || soloSide === compareVisualSide) && (!isSharedFrameMode || compareHasRenderableMedia || soloSide === compareVisualSide);

	const visualAOpacity = isWipeMode ? wipeTopOpacity : 1;
	const visualBOpacity = compareWarmupActive ? 0 : isOverlayMode ? overlayOpacity : 1;

	const primaryOpacity = swapVisualSides ? visualBOpacity : visualAOpacity;
	const compareOpacity = swapVisualSides ? visualAOpacity : visualBOpacity;

	const visualAZIndex = isWipeMode ? 20 : 10;
	const visualBZIndex = isWipeMode ? 10 : 20;

	const primaryZIndex = swapVisualSides ? visualBZIndex : visualAZIndex;
	const compareZIndex = swapVisualSides ? visualAZIndex : visualBZIndex;

	const getBadgeLeft = React.useCallback(
		(rect: Rect, visualSide: "left" | "right") => {
			if (!isUsableRect(rect)) {
				return 8;
			}

			if (visualSide === "right" && (!isStacked || isSharedFrameMode)) {
				return undefined;
			}

			return rect.left + 8;
		},
		[isSharedFrameMode, isStacked]
	);

	const getBadgeRight = React.useCallback(
		(rect: Rect, visualSide: "left" | "right") => {
			if (!isUsableRect(rect)) {
				return undefined;
			}

			if (visualSide === "right" && (!isStacked || isSharedFrameMode)) {
				return Math.max(stageSize.width - rect.left - rect.width + 8, 8);
			}

			return undefined;
		},
		[isSharedFrameMode, isStacked, stageSize.width]
	);

	const primaryBadgeTop = isUsableRect(primaryRect) ? primaryRect.top + 8 : 8;
	const compareBadgeTop = isUsableRect(compareRect) ? compareRect.top + 8 : 8;

	const primaryBadgeLeft = getBadgeLeft(primaryRect, primaryVisualSide);
	const primaryBadgeRight = getBadgeRight(primaryRect, primaryVisualSide);

	const compareBadgeLeft = getBadgeLeft(compareRect, compareVisualSide);
	const compareBadgeRight = getBadgeRight(compareRect, compareVisualSide);

	const primarySideLabel = swapVisualSides ? "B" : "A";
	const compareSideLabel = swapVisualSides ? "A" : "B";

	const primaryFallbackLabel = swapVisualSides ? "Compare" : "Primary";
	const compareFallbackLabel = swapVisualSides ? "Primary" : "Compare";

	const primaryBadgeColor = soloSide !== "none" && soloSide !== primaryVisualSide ? "rgba(255,255,255,0.45)" : "white";
	const compareBadgeColor = soloSide !== "none" && soloSide !== compareVisualSide ? "rgba(255,255,255,0.45)" : "white";

	const visualBRect = swapVisualSides ? primaryRect : compareRect;

	const showWipeOpacityIndicator = isWipeMode && wipeTopOpacity < 0.995 && isUsableRect(visualBRect);
	const wipeOpacityIndicatorLabel = `${Math.round(wipeTopOpacity * 100)}%`;
	const wipeOpacityIndicatorLeft = showWipeOpacityIndicator ? visualBRect.left + 8 : 8;
	const wipeOpacityIndicatorTop = showWipeOpacityIndicator ? Math.max(visualBRect.top + visualBRect.height - 36, visualBRect.top + 8) : 8;

	const handleWipePointerMove = React.useCallback((evt: React.PointerEvent<HTMLDivElement>) => {
		const rect = evt.currentTarget.getBoundingClientRect();

		if (!rect.width || !rect.height) {
			return;
		}

		const x = clamp((evt.clientX - rect.left) / rect.width, 0, 1);
		const dx = evt.clientX - wipeDragRef.current.startX;
		const dy = evt.clientY - wipeDragRef.current.startY;

		if (Math.sqrt(dx * dx + dy * dy) > 4) {
			wipeDragRef.current.moved = true;
		}

		setWipePosition(x);
	}, []);

	const handleOverlayWheel = React.useCallback((evt: React.WheelEvent<HTMLDivElement>) => {
		evt.preventDefault();
		evt.stopPropagation();

		const delta = evt.deltaY || evt.deltaX || 0;
		const opacityStep = evt.shiftKey ? 0.0002 : 0.0007;

		setOverlayOpacity((prev) => {
			return clamp(prev - delta * opacityStep, 0, 1);
		});
	}, []);

	const handleWipeOpacityWheel = React.useCallback((evt: React.WheelEvent<HTMLDivElement>) => {
		evt.preventDefault();
		evt.stopPropagation();

		const delta = evt.deltaY || evt.deltaX || 0;
		const opacityStep = evt.shiftKey ? 0.0002 : 0.0007;

		setWipeTopOpacity((prev) => {
			return clamp(prev - delta * opacityStep, 0, 1);
		});
	}, []);

	const handleWipePointerDown = React.useCallback(
		(evt: React.PointerEvent<HTMLDivElement>) => {
			evt.preventDefault();
			evt.stopPropagation();

			wipeDragRef.current = {
				pointerId: evt.pointerId,
				startX: evt.clientX,
				startY: evt.clientY,
				moved: false,
			};

			try {
				evt.currentTarget.setPointerCapture(evt.pointerId);
			} catch {}

			handleWipePointerMove(evt);
		},
		[handleWipePointerMove]
	);

	const handleWipePointerUp = React.useCallback(
		(evt: React.PointerEvent<HTMLDivElement>) => {
			evt.preventDefault();
			evt.stopPropagation();

			try {
				evt.currentTarget.releasePointerCapture(evt.pointerId);
			} catch {}

			if (!wipeDragRef.current.moved) {
				handleTogglePlayPause();
			}
		},
		[handleTogglePlayPause]
	);

	const handleWipeDoubleClick = React.useCallback(
		(evt: React.MouseEvent<HTMLDivElement>) => {
			evt.preventDefault();
			evt.stopPropagation();
			resetWipe();
		},
		[resetWipe]
	);

	return (
		<Box ref={stageRef} sx={{ ...nonselect, position: "absolute", top: 51, left: 0, width: "100%", height: "calc(100% - 196px - 51px)", backgroundColor: "black", overflow: "hidden" }}>
			<ComparisonModeControls mode={comparisonMode} soloSide={soloSide} onModeChange={setComparisonMode} onSoloSideChange={setSoloSide} onResetWipe={resetWipe} />

			{!isSharedFrameMode ? (
				<Box sx={{ position: "absolute", top: isStacked ? "50%" : 0, left: isStacked ? 0 : "50%", width: isStacked ? "100%" : "1px", height: isStacked ? "1px" : "100%", backgroundColor: "rgba(255,255,255,0.12)", zIndex: 5 }} />
			) : null}

			<MediaFrame
				rect={primaryRect}
				media={primary}
				kind="primary"
				visible={showPrimary}
				opacity={primaryOpacity}
				zIndex={primaryZIndex}
				clipPath={primaryClipPath}
				overscanZoomEnabled={overscanZoomEnabled}
				videoFlipped={videoFlipped}
				playerRef={playerRef}
				muted={muted}
				playingValue={playing.current}
				autoPlay={prefPlaying}
				looping={looping && !loopRangeActive}
				seeking={seeking}
				setBuffering={setBuffering}
				play={play}
				pause={pause}
				handleOnReady={handleOnReady}
				handleProgress={handleProgress}
				handleEnded={handleEnded}
			/>

			<MediaFrame
				rect={compareRect}
				media={compare}
				kind="compare"
				visible={showCompare}
				opacity={compareOpacity}
				zIndex={compareZIndex}
				clipPath={compareClipPath}
				overscanZoomEnabled={overscanZoomEnabled}
				videoFlipped={videoFlipped}
				comparePlayerRef={comparePlayerRef}
				handleCompareReady={handleCompareReady}
				comparePlayingValue={compareWarmupActive}
				comparePlaybackRate={compareWarmupActive ? COMPARE_WARMUP_PLAYBACK_RATE : 1}
				handleCompareProgress={handleCompareProgress}
				handleCompareError={handleCompareError}
			/>

			{isSharedFrameMode ? (
				<FrameDecorations rect={decorationRect} media={activeMedia} infoState={infoState} gridState={gridState} overscanZoomEnabled={overscanZoomEnabled} />
			) : (
				<>
					<FrameDecorations rect={primaryRect} media={primary} infoState={infoState} gridState={gridState} overscanZoomEnabled={overscanZoomEnabled} />
					<FrameDecorations rect={compareRect} media={compare} infoState={infoState} gridState={gridState} overscanZoomEnabled={overscanZoomEnabled} />
				</>
			)}

			{isWipeMode && isUsableRect(visualBRect) ? (
				<Box
					onPointerDown={handleWipePointerDown}
					onPointerMove={(evt) => {
						if (evt.buttons === 1) {
							handleWipePointerMove(evt);
						}
					}}
					onPointerUp={handleWipePointerUp}
					onWheel={handleWipeOpacityWheel}
					onDoubleClick={handleWipeDoubleClick}
					sx={{ position: "absolute", left: visualBRect.left, top: visualBRect.top, width: visualBRect.width, height: visualBRect.height, zIndex: 65, cursor: "ew-resize", touchAction: "none" }}
				>
					<svg width="100%" height="100%" style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "visible" }}>
						<line x1={`${topX * 100}%`} y1="0%" x2={`${bottomX * 100}%`} y2="100%" stroke="rgb(16, 185, 129)" strokeWidth="2" />
					</svg>
				</Box>
			) : null}

			{showWipeOpacityIndicator ? (
				<Box
					sx={{
						position: "absolute",
						left: wipeOpacityIndicatorLeft,
						top: wipeOpacityIndicatorTop,
						zIndex: 75,
						px: 1,
						py: 0.35,
						borderRadius: 1,
						backgroundColor: "rgba(0,0,0,0.72)",
						color: "white",
						pointerEvents: "none",
						display: "flex",
						gap: 0.5,
						alignItems: "center",
						border: "1px solid rgba(16,185,129,0.65)",
					}}
				>
					<Typography variant="caption" sx={{ fontWeight: 700 }}>
						Wipe overlay
					</Typography>

					<Typography variant="caption" sx={{ opacity: 0.85 }}>
						{wipeOpacityIndicatorLabel}
					</Typography>
				</Box>
			) : null}

			{comparisonMode !== "wipe" || soloSide !== "none" ? (
				<Box
					onClick={handleTogglePlayPause}
					onWheel={isOverlayMode ? handleOverlayWheel : undefined}
					sx={{
						position: "absolute",
						inset: 0,
						zIndex: 55,
						cursor: primary?.previewAssetIsVideo ? "pointer" : "default",
					}}
				/>
			) : null}

			{isUsableRect(primaryRect) ? <ComparisonBadge media={primary} fallbackLabel={primaryFallbackLabel} sideLabel={primarySideLabel} left={primaryBadgeLeft} right={primaryBadgeRight} top={primaryBadgeTop} color={primaryBadgeColor} /> : null}

			{compare && isUsableRect(compareRect) ? (
				<ComparisonBadge media={compare} fallbackLabel={compareFallbackLabel} sideLabel={compareSideLabel} left={compareBadgeLeft} right={compareBadgeRight} top={compareBadgeTop} color={compareBadgeColor} />
			) : null}
		</Box>
	);
}
